import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

function Label({ children }) {
  return <div className="label-mono mb-1.5">{children}</div>;
}

export default function LeadCTA({ sourcePage = "landing", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    message: "",
    callback: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name, email: form.email, phone: form.phone,
        message: form.message, type: form.callback ? "callback" : "contact",
        source_page: sourcePage,
      });
      toast.success("Sent — we'll be in touch within 24h.");
      setOpen(false);
      setForm({ ...form, message: "" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid={`lead-cta-${sourcePage}`}
          className={className}
        >
          Help us increase your business <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md rounded-none border-ink no-round" data-testid="lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest text-2xl">Help us grow your business.</DialogTitle>
          <DialogDescription className="text-ink/60">
            Tell us about your store. We'll get back within 24 hours with concrete next steps.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input data-testid="lead-name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="lead-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div>
            <Label>What do you need help with?</Label>
            <Textarea data-testid="lead-message" rows={3}
              value={form.message} onChange={(e) => set("message", e.target.value)}
              placeholder="e.g. listings not converting, need keyword strategy…" />
          </div>
          <div className="flex items-center justify-between border border-rule px-4 py-3">
            <div>
              <div className="text-sm font-medium text-ink">Request a callback</div>
              <div className="text-xs text-ink/50 mt-0.5">20-minute consult.</div>
            </div>
            <Switch checked={form.callback} onCheckedChange={(v) => set("callback", v)} data-testid="lead-callback-switch" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="w-full" data-testid="lead-submit-btn">
              {submitting ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
